import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";

import { useEffectOnce } from "@js/apps/common/hooks/use-effect-once";

import styles from "./style.module.scss";

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  useEffectOnce(() => {
    const errorListener = (event: PromiseRejectionEvent) => {
      event.preventDefault();
      const safeStringify = (value: unknown) => {
        try {
          return JSON.stringify(value);
        } catch {
          return "[unserializable]";
        }
      };

      const prepareError = (errorReason: unknown) => {
        if (!errorReason) {
          return new Error("[none]");
        }

        if (errorReason instanceof Error) {
          return errorReason;
        }

        if (typeof errorReason === "string") {
          return new Error(errorReason);
        }

        return new Error(safeStringify(errorReason));
      };

      // Send richer data to Sentry
      Sentry.captureException(prepareError(event?.reason), {
        extra: {
          reason: event.reason,
          promise: event.promise,
        },
      });
    };
    window.addEventListener("unhandledrejection", errorListener);
    return () => {
      window.removeEventListener("unhandledrejection", errorListener);
    };
  });

  return (
    <Sentry.ErrorBoundary fallback={<Fallback />}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

const Fallback = () => {
  const reloadPage = () => window.location.reload();

  return (
    <div className={styles.errorBoundary}>
      <a href="/">
        <img
          src={`${SETTINGS.STATIC_URL}logo/logo-symbol.svg`}
          alt=""
          className={styles.errorBoundaryLogo}
          width="47"
          height="45"
        />
      </a>
      <p className={styles.errorBoundaryTitle}>
        Sorry - an unexpected error occurred.
        <br />
        Please try reloading the page.
      </p>
      <button onClick={reloadPage} className={styles.errorBoundaryButton}>
        Refresh page
      </button>
    </div>
  );
};
